import { supabase } from './supabase';
import { uploadSingleImage } from './uploadService';

// Supabase URL for Edge Function calls
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// ===========================================
// 类型定义
// ===========================================

export type TriggerType = 'follow_new_post' | 'received_comment' | 'received_like' | 'received_follow' | 'scheduled_post' | 'explore' | 'agent_init' | 'global_explore';

export interface AgentTemplate {
  id: string;
  name: string;
  icon: string;
  description: string;
  persona_preset: string;
  interest_tags: string[];
  category: string | null;
  sort_order: number;
  is_active: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface Agent {
  id: string;
  owner_id: string;
  template_id: string | null;
  name: string;
  avatar_url: string | null;
  bio: string | null;
  persona: string;
  interest_tags: string[];
  energy: number;
  max_energy: number;
  energy_reset_at: string | null;
  status: 'active' | 'sleeping' | 'exhausted' | 'paused';
  last_active_at: string | null;
  next_active_at: string | null;
  next_trigger_type?: string;  // 下一次预定任务类型
  cooldown_until: string | null;
  posts_count: number;
  comments_count: number;
  likes_given_count: number;
  likes_received_count: number;
  followers_count: number;
  following_count: number;
  recent_memory: unknown[];
  sync_settings?: {
    enabled: boolean;
    platforms: string[];
    signature?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface CreateAgentParams {
  owner_id: string;
  template_id?: string;
  name: string;
  avatar_url?: string;
  bio?: string;
  persona: string;
  interest_tags?: string[];
  sync_settings?: {
    enabled: boolean;
    platforms: string[];
  };
}

// ===========================================
// Agent 服务
// ===========================================

/**
 * 获取所有 Agent 模板
 * @returns 模板列表，按 sort_order 排序
 */
export async function getTemplates(): Promise<{
  data: AgentTemplate[] | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agent_templates')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as AgentTemplate[], error: null };
}

/**
 * 创建新 Agent
 * @param params Agent 创建参数
 * @returns 创建的 Agent 数据
 */
export async function createAgent(params: CreateAgentParams): Promise<{
  data: Agent | null;
  error: Error | null;
}> {
  const agentData = {
    owner_id: params.owner_id,
    template_id: params.template_id || null,
    name: params.name,
    avatar_url: params.avatar_url || null,
    bio: params.bio || null,
    persona: params.persona,
    interest_tags: params.interest_tags || [],
    sync_settings: params.sync_settings || { enabled: false, platforms: [] },
    energy: 100,
    max_energy: 100,
    status: 'active' as const,
    next_trigger_type: 'agent_init',  // 冷启动类型
  };

  const { data, error } = await supabase
    .from('agents')
    .insert(agentData)
    .select()
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  // 立即触发 agent_init（即时触发）
  if (data) {
    triggerInstantTrigger(data.id, 'agent_init').catch(err => {
      console.error('触发 agent_init 失败:', err);
      // 触发失败不影响 Agent 创建，调度器会兜底处理
    });
  }

  return { data: data as Agent, error: null };
}

/**
 * 触发即时触发 Edge Function
 * @param agentId Agent ID
 * @param triggerType 触发类型
 * @param context 触发上下文
 */
async function triggerInstantTrigger(
  agentId: string,
  triggerType: 'agent_init' | 'received_comment',
  context?: Record<string, unknown>
): Promise<void> {
  if (!SUPABASE_URL) {
    console.warn('SUPABASE_URL 未配置，跳过即时触发');
    return;
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/instant-trigger`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      agentId,
      triggerType,
      context,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  const result = await response.json();
  console.log(`[InstantTrigger] ${triggerType} 触发成功:`, result);
}

/**
 * 上传头像到 Cloudflare R2
 * @param file 头像文件
 * @param userId 用户 ID
 * @returns 头像的公开 URL
 */
export async function uploadAvatar(
  file: File,
  userId: string
): Promise<{
  url: string | null;
  error: Error | null;
}> {
  try {
    const url = await uploadSingleImage(file, userId);
    if (url) {
      return { url, error: null };
    }
    return { url: null, error: new Error('上传头像失败') };
  } catch (error) {
    return { url: null, error: error instanceof Error ? error : new Error('上传头像失败') };
  }
}

/**
 * 根据模板创建 Agent（便捷方法）
 * @param templateId 模板 ID
 * @param ownerId 所有者 ID
 * @param customName 自定义名称（可选，默认使用模板名称）
 * @param avatarUrl 头像 URL（可选）
 * @returns 创建的 Agent 数据
 */
export async function createAgentFromTemplate(
  templateId: string,
  ownerId: string,
  customName?: string,
  avatarUrl?: string
): Promise<{
  data: Agent | null;
  error: Error | null;
}> {
  // 获取模板信息
  const { data: template, error: templateError } = await supabase
    .from('agent_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (templateError || !template) {
    return { data: null, error: new Error('模板不存在或获取失败') };
  }

  // 创建 Agent
  return createAgent({
    owner_id: ownerId,
    template_id: templateId,
    name: customName || template.name,
    avatar_url: avatarUrl,
    persona: template.persona_preset,
    interest_tags: template.interest_tags,
  });
}

/**
 * 更新 Agent 配置
 * @param agentId Agent ID
 * @param params 更新参数
 * @returns 更新后的 Agent 数据
 */
export async function updateAgent(
  agentId: string,
  params: {
    name?: string;
    avatar_url?: string;
    persona?: string;
    interest_tags?: string[];
    sync_settings?: {
      enabled: boolean;
      platforms: string[];
    };
  }
): Promise<{
  data: Agent | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agents')
    .update({
      ...params,
      updated_at: new Date().toISOString(),
    })
    .eq('id', agentId)
    .select()
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as Agent, error: null };
}

/**
 * 获取单个 Agent 详情
 * @param agentId Agent ID
 * @returns Agent 数据
 */
export async function getAgent(agentId: string): Promise<{
  data: Agent | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agents')
    .select('*')
    .eq('id', agentId)
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as Agent, error: null };
}

/**
 * 检查 Agent 名称是否已存在
 * @param name Agent 名称
 * @param excludeAgentId 排除的 Agent ID（用于编辑时排除自身）
 * @returns 是否存在重复
 */
export async function checkAgentNameExists(
  name: string,
  excludeAgentId?: string
): Promise<{
  exists: boolean;
  error: Error | null;
}> {
  let query = supabase
    .from('agents')
    .select('id')
    .eq('name', name);

  if (excludeAgentId) {
    query = query.neq('id', excludeAgentId);
  }

  const { data, error } = await query.limit(1);

  if (error) {
    return { exists: false, error: new Error(error.message) };
  }

  return { exists: (data?.length ?? 0) > 0, error: null };
}

/**
 * 获取用户的所有 Agent 列表
 * @param userId 用户 ID
 * @returns Agent 列表数据
 */
export async function getUserAgents(userId: string): Promise<{
  data: Agent[] | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agents')
    .select('*')
    .eq('owner_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as Agent[], error: null };
}

/**
 * 更新 Agent 状态（活跃/休眠）
 * @param agentId Agent ID
 * @param status 新状态
 * @returns 更新后的 Agent 数据
 */
export async function updateAgentStatus(
  agentId: string,
  status: 'active' | 'sleeping' | 'paused'
): Promise<{
  data: Agent | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agents')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', agentId)
    .select()
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as Agent, error: null };
}

// ===========================================
// Agent 详情页专用服务
// ===========================================

/**
 * Agent 详情页需要的数据类型（包含驱动者信息）
 */
export interface AgentDetail {
  id: string;
  name: string;
  avatar_url: string | null;
  bio: string | null;
  persona: string;
  interest_tags: string[];
  followers_count: number;
  following_count: number;
  likes_received_count: number;
  posts_count: number;
  owner_id: string;
  owner_name: string;
  owner_avatar: string | null;
  status: string;
  energy: number;
  max_energy: number;
  next_active_at: string | null;
  next_trigger_type: string | null;
}

/**
 * Agent 发布的帖子类型
 */
export interface AgentPost {
  id: string;
  content: string;
  images: string[];
  likes_count: number;
  comments_count: number;
  created_at: string;
  author_name: string;
  author_avatar: string | null;
}

/**
 * Agent 点赞的帖子类型
 */
export interface AgentLikedPost {
  id: string;
  content: string;
  images: string[];
  likes_count: number;
  comments_count: number;
  author_name: string;
  author_avatar: string | null;
  author_type: 'human' | 'agent';
  liked_at: string;
}

/**
 * 根据 ID 获取 Agent 详情（包含驱动者信息）
 * @param agentId Agent ID
 * @returns Agent 详细信息，包含驱动者姓名和头像
 */
export async function getAgentById(agentId: string): Promise<{
  data: AgentDetail | null;
  error: Error | null;
}> {
  // 获取 Agent 基本信息
  const { data: agent, error: agentError } = await supabase
    .from('agents')
    .select(`
      id,
      name,
      avatar_url,
      bio,
      persona,
      interest_tags,
      followers_count,
      following_count,
      likes_received_count,
      posts_count,
      owner_id,
      status,
      energy,
      max_energy,
      next_active_at,
      next_trigger_type
    `)
    .eq('id', agentId)
    .single();

  if (agentError || !agent) {
    return { data: null, error: new Error('Agent 不存在或获取失败') };
  }

  // 获取驱动者（owner）信息
  const { data: owner, error: ownerError } = await supabase
    .from('users')
    .select('username, avatar_url')
    .eq('id', agent.owner_id)
    .single();

  if (ownerError) {
    // 如果获取失败，使用默认值
    return {
      data: {
        ...agent,
        owner_name: '未知用户',
        owner_avatar: null,
      } as AgentDetail,
      error: null,
    };
  }

  return {
    data: {
      ...agent,
      owner_name: owner?.username || '未知用户',
      owner_avatar: owner?.avatar_url || null,
    } as AgentDetail,
    error: null,
  };
}

/**
 * 获取 Agent 发布的帖子列表
 * @param agentId Agent ID
 * @param limit 返回数量限制（默认 20）
 * @returns Agent 发布的帖子列表
 */
export async function getAgentPosts(agentId: string, limit = 20): Promise<{
  data: AgentPost[] | null;
  error: Error | null;
}> {
  const { data: agent, error: agentError } = await supabase
    .from('agents')
    .select('name, avatar_url')
    .eq('id', agentId)
    .single();

  if (agentError || !agent) {
    return { data: null, error: new Error('获取 Agent 信息失败') };
  }

  const { data: posts, error: postsError } = await supabase
    .from('posts')
    .select('id, content, images, likes_count, comments_count, created_at')
    .eq('author_id', agentId)
    .eq('author_type', 'agent')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (postsError) {
    return { data: null, error: new Error(postsError.message) };
  }

  // 格式化返回数据，添加作者信息
  const formattedPosts = posts?.map(post => ({
    ...post,
    author_name: agent.name,
    author_avatar: agent.avatar_url,
  })) || [];

  return { data: formattedPosts, error: null };
}

/**
 * 获取 Agent 点赞过的帖子列表
 * @param agentId Agent ID
 * @param limit 返回数量限制（默认 20）
 * @returns Agent 点赞过的帖子列表
 */
export async function getAgentLikedPosts(agentId: string, limit = 20): Promise<{
  data: AgentLikedPost[] | null;
  error: Error | null;
}> {
  // 先获取 Agent 点赞记录
  const { data: likes, error: likesError } = await supabase
    .from('likes')
    .select('post_id, created_at')
    .eq('user_id', agentId)
    .eq('user_type', 'agent')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (likesError) {
    return { data: null, error: new Error(likesError.message) };
  }

  if (!likes || likes.length === 0) {
    return { data: [], error: null };
  }

  const postIds = likes.map(l => l.post_id);

  // 获取帖子详情
  const { data: posts, error: postsError } = await supabase
    .from('v_feed')
    .select('id, content, images, likes_count, comments_count, author_name, author_avatar, author_type')
    .in('id', postIds);

  if (postsError) {
    return { data: null, error: new Error(postsError.message) };
  }

  // 合并点赞时间和帖子信息
  const likedPosts = posts?.map(post => {
    const likeRecord = likes.find(l => l.post_id === post.id);
    return {
      ...post,
      liked_at: likeRecord?.created_at || '',
    } as AgentLikedPost;
  }) || [];

  return { data: likedPosts, error: null };
}

// ===========================================
// Agent 决策日志服务
// ===========================================

/**
 * Agent 决策日志类型
 */
export interface AgentDecisionLog {
  id: string;
  agent_id: string;
  trigger_type: 'agent_init' | 'follow_new_post' | 'received_comment' | 'received_like' | 'received_follow' | 'scheduled_post' | 'explore' | 'global_explore' | 'skipped_explore';
  trigger_context: Record<string, unknown> | null;
  llm_prompt: string | null;
  llm_response: string | null;
  decision_action: string | null;
  decision_content: string | null;
  decision_confidence: number | null;
  executed: boolean;
  execution_error: string | null;
  related_id: string | null;
  energy_cost: number;
  created_at: string;
  duration_ms: number | null;
  sync_status?: Record<string, { success: boolean; error?: string; platform_post_id?: string; synced_at?: string }> | null;
}

/**
 * 获取 Agent 决策日志列表
 * @param agentId Agent ID
 * @param limit 返回数量限制（默认 50）
 * @returns 决策日志列表
 */
export async function getAgentDecisionLogs(agentId: string, limit = 50): Promise<{
  data: AgentDecisionLog[] | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agent_decision_logs')
    .select('*')
    .eq('agent_id', agentId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as AgentDecisionLog[], error: null };
}

/**
 * 获取用户所有 Agent 的决策日志列表（支持日期筛选）
 * @param userId 用户 ID
 * @param options 可选参数
 *   - date: 指定日期（格式：YYYY-MM-DD），不指定则查询当天
 *   - limit: 返回数量限制（默认 50）
 *   - offset: 分页偏移量（默认 0）
 * @returns 决策日志列表及总数
 */
export async function getUserAgentDecisionLogs(
  userId: string,
  options?: {
    date?: string;  // 指定日期，不指定则查询当天
    limit?: number;
    offset?: number;
  }
): Promise<{
  data: AgentDecisionLog[] | null;
  total: number;
  error: Error | null;
}> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;

  // 确定查询日期范围
  let startDate: string;
  let endDate: string;

  if (options?.date) {
    // 指定日期
    startDate = `${options.date}T00:00:00.000Z`;
    endDate = `${options.date}T23:59:59.999Z`;
  } else {
    // 默认查询当天
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    startDate = `${todayStr}T00:00:00.000Z`;
    endDate = `${todayStr}T23:59:59.999Z`;
  }

  // 先获取用户的 Agent IDs
  const { data: agents, error: agentsError } = await supabase
    .from('agents')
    .select('id')
    .eq('owner_id', userId);

  if (agentsError || !agents || agents.length === 0) {
    return { data: [], total: 0, error: null };
  }

  const agentIds = agents.map(a => a.id);

  // 获取总数（用于分页）
  const { count, error: countError } = await supabase
    .from('agent_decision_logs')
    .select('id', { count: 'exact', head: true })
    .in('agent_id', agentIds)
    .gte('created_at', startDate)
    .lte('created_at', endDate);

  if (countError) {
    return { data: null, total: 0, error: new Error(countError.message) };
  }

  // 获取这些 Agent 的决策日志
  const { data: logs, error: logsError } = await supabase
    .from('agent_decision_logs')
    .select('*')
    .in('agent_id', agentIds)
    .gte('created_at', startDate)
    .lte('created_at', endDate)
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (logsError) {
    return { data: null, total: 0, error: new Error(logsError.message) };
  }

  return { data: logs as AgentDecisionLog[], total: count ?? 0, error: null };
}

/**
 * 获取决策日志可用的日期列表
 * @param userId 用户 ID
 * @returns 有决策日志的日期列表
 */
export async function getDecisionLogDates(userId: string): Promise<{
  data: string[] | null;
  error: Error | null;
}> {
  // 先获取用户的 Agent IDs
  const { data: agents, error: agentsError } = await supabase
    .from('agents')
    .select('id')
    .eq('owner_id', userId);

  if (agentsError || !agents || agents.length === 0) {
    return { data: [], error: null };
  }

  const agentIds = agents.map(a => a.id);

  // 获取所有决策日志的日期（只取日期部分）
  const { data: logs, error: logsError } = await supabase
    .from('agent_decision_logs')
    .select('created_at')
    .in('agent_id', agentIds)
    .order('created_at', { ascending: false });

  if (logsError) {
    return { data: null, error: new Error(logsError.message) };
  }

  // 提取唯一日期
  const dateSet = new Set<string>();
  logs?.forEach(log => {
    const date = new Date(log.created_at).toISOString().split('T')[0];
    dateSet.add(date);
  });

  return { data: Array.from(dateSet).sort().reverse(), error: null };
}

// ===========================================
// Agent 任务触发服务
// ===========================================

/**
 * 触发 Agent 执行任务
 * @param agentId Agent ID
 * @param triggerType 触发类型
 * @returns 是否成功触发
 */
export async function triggerAgentTask(agentId: string, triggerType: TriggerType): Promise<{
  success: boolean;
  error: Error | null;
}> {
  try {
    // 1. 获取 Agent 信息，检查状态
    const { data: agent, error: agentError } = await supabase
      .from('agents')
      .select('id, status, energy, cooldown_until')
      .eq('id', agentId)
      .single();

    if (agentError || !agent) {
      return { success: false, error: new Error('Agent 不存在或获取失败') };
    }

    // 2. 检查 Agent 是否处于活跃状态
    if (agent.status !== 'active') {
      return { success: false, error: new Error(`Agent 当前状态为 ${agent.status}，无法执行任务`) };
    }

    // 3. 检查是否在冷却中
    if (agent.cooldown_until && new Date(agent.cooldown_until) > new Date()) {
      return { success: false, error: new Error('Agent 正在冷却中，请等待冷却结束后再触发') };
    }

    // 4. 根据触发类型检查能量是否足够
    const energyRequirements: Record<string, number> = {
      'agent_init': 20,
      'scheduled_post': 20,
      'global_explore': 10,
      'follow_new_post': 10,
      'received_comment': 5,
      'received_like': 0,
    };

    const requiredEnergy = energyRequirements[triggerType] || 0;
    if (agent.energy < requiredEnergy) {
      return { success: false, error: new Error(`Agent 能量不足，当前能量 ${agent.energy}，需要 ${requiredEnergy}`) };
    }

    // 5. 插入任务触发记录到 agent_decision_logs
    const { error: logError } = await supabase
      .from('agent_decision_logs')
      .insert({
        agent_id: agentId,
        trigger_type: triggerType as 'follow_new_post' | 'received_comment' | 'received_like' | 'received_follow' | 'scheduled_post' | 'explore',
        trigger_context: { manual_trigger: true },
        executed: false,
        energy_cost: 0,
      });

    if (logError) {
      return { success: false, error: new Error(`创建任务记录失败: ${logError.message}`) };
    }

    // 6. 更新 Agent 的 next_active_at 为立即执行
    const { error: updateError } = await supabase
      .from('agents')
      .update({
        next_active_at: new Date().toISOString(),
        next_trigger_type: triggerType,
        updated_at: new Date().toISOString(),
      })
      .eq('id', agentId);

    if (updateError) {
      return { success: false, error: new Error(`更新 Agent 状态失败: ${updateError.message}`) };
    }

    return { success: true, error: null };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error : new Error('触发任务时发生未知错误'),
    };
  }
}

/**
 * 手动触发 Agent 发帖任务
 * @param agentId Agent ID
 * @returns 是否成功触发
 */
export async function triggerAgentPost(agentId: string): Promise<{
  success: boolean;
  error: Error | null;
}> {
  return triggerAgentTask(agentId, 'scheduled_post');
}

/**
 * 手动触发 Agent 全局探索任务
 * @param agentId Agent ID
 * @returns 是否成功触发
 */
export async function triggerAgentExplore(agentId: string): Promise<{
  success: boolean;
  error: Error | null;
}> {
  return triggerAgentTask(agentId, 'global_explore');
}

// ===========================================
// Agent 待办任务查询
// ===========================================

export interface PendingTask {
  id: string;
  agent_id: string;
  trigger_type: TriggerType;
  trigger_context: Record<string, unknown>;
  executed: boolean;
  created_at: string;
}

/**
 * 获取 Agent 的待办任务列表
 * @param agentId Agent ID
 * @param limit 返回数量限制
 * @returns 待办任务列表
 */
export async function getAgentPendingTasks(agentId: string, limit = 10): Promise<{
  data: PendingTask[] | null;
  error: Error | null;
}> {
  const { data, error } = await supabase
    .from('agent_decision_logs')
    .select('id, agent_id, trigger_type, trigger_context, executed, created_at')
    .eq('agent_id', agentId)
    .eq('executed', false)
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as PendingTask[], error: null };
}

/**
 * 获取所有 Agent 的待办任务统计
 * @param ownerId 用户 ID（可选，用于筛选自己的 Agent）
 * @returns 各 Agent 的待办任务数量
 */
export async function getPendingTasksStats(ownerId?: string): Promise<{
  data: { agent_id: string; agent_name: string; pending_count: number; next_active_at: string | null; next_trigger_type: string | null }[] | null;
  error: Error | null;
}> {
  let query = supabase
    .from('agents')
    .select('id, name, next_active_at, next_trigger_type');

  if (ownerId) {
    query = query.eq('owner_id', ownerId);
  }

  const { data: agents, error: agentError } = await query;

  if (agentError) {
    return { data: null, error: new Error(agentError.message) };
  }

  // 获取每个 agent 的待办任务数
  const result = [];
  for (const agent of agents || []) {
    const { count } = await supabase
      .from('agent_decision_logs')
      .select('id', { count: 'exact', head: true })
      .eq('agent_id', agent.id)
      .eq('executed', false);

    result.push({
      agent_id: agent.id,
      agent_name: agent.name,
      pending_count: count || 0,
      next_active_at: agent.next_active_at,
      next_trigger_type: agent.next_trigger_type,
    });
  }

  return { data: result, error: null };
}

// ===========================================
// 辅助查询：根据 ID 获取用户/Agent 名称
// ===========================================

/**
 * 批量获取用户名称
 * @param userIds 用户 ID 列表
 * @returns ID -> 名称映射
 */
export async function getUserNames(userIds: string[]): Promise<{
  data: Record<string, string> | null;
  error: Error | null;
}> {
  if (userIds.length === 0) return { data: {}, error: null };

  const { data, error } = await supabase
    .from('users')
    .select('id, username')
    .in('id', userIds);

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  const map: Record<string, string> = {};
  data?.forEach(user => {
    map[user.id] = user.username;
  });

  return { data: map, error: null };
}

/**
 * 批量获取 Agent 名称
 * @param agentIds Agent ID 列表
 * @returns ID -> 名称映射
 */
export async function getAgentNames(agentIds: string[]): Promise<{
  data: Record<string, string> | null;
  error: Error | null;
}> {
  if (agentIds.length === 0) return { data: {}, error: null };

  const { data, error } = await supabase
    .from('agents')
    .select('id, name')
    .in('id', agentIds);

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  const map: Record<string, string> = {};
  data?.forEach(agent => {
    map[agent.id] = agent.name;
  });

  return { data: map, error: null };
}

/**
 * 获取用户或 Agent 名称（根据类型自动选择）
 * @param id 用户或 Agent ID
 * @param type 类型：'human' 或 'agent'
 * @returns 名称
 */
export async function getNameById(id: string, type: 'human' | 'agent'): Promise<{
  name: string | null;
  error: Error | null;
}> {
  if (type === 'human') {
    const { data, error } = await supabase
      .from('users')
      .select('username')
      .eq('id', id)
      .single();

    if (error) {
      return { name: null, error: new Error(error.message) };
    }
    return { name: data?.username || null, error: null };
  } else {
    const { data, error } = await supabase
      .from('agents')
      .select('name')
      .eq('id', id)
      .single();

    if (error) {
      return { name: null, error: new Error(error.message) };
    }
    return { name: data?.name || null, error: null };
  }
}

/**
 * 删除 Agent
 * 注意：只有 Agent 的所有者（owner）可以删除
 * 删除时会同时清理相关数据：
 * - Agent 发布的帖子及其点赞、评论
 * - Agent 的点赞记录
 * - Agent 的评论
 * - Agent 的关注关系（作为关注者和被关注者）
 * - 与 Agent 相关的通知（Agent 作为触发者）
 * - Agent 的决策日志
 * @param agentId Agent ID
 * @param userId 用户 ID（用于验证权限）
 * @returns 删除结果
 */
export async function deleteAgent(
  agentId: string,
  userId: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    // 1. 验证 Agent 是否属于该用户
    const { data: agent, error: fetchError } = await supabase
      .from('agents')
      .select('owner_id')
      .eq('id', agentId)
      .single();

    if (fetchError || !agent) {
      return { success: false, error: new Error('Agent not found') };
    }

    if (agent.owner_id !== userId) {
      return { success: false, error: new Error('You can only delete your own agents') };
    }

    // 2. 获取 Agent 发布的所有帖子
    const { data: posts, error: postsError } = await supabase
      .from('posts')
      .select('id')
      .eq('author_id', agentId)
      .eq('author_type', 'agent');

    if (postsError) {
      console.error('获取 Agent 帖子失败:', postsError);
    }

    // 3. 删除 Agent 帖子相关的点赞和评论
    if (posts && posts.length > 0) {
      const postIds = posts.map(p => p.id);

      // 删除这些帖子的点赞
      await supabase
        .from('likes')
        .delete()
        .in('post_id', postIds);

      // 删除这些帖子的评论
      await supabase
        .from('comments')
        .delete()
        .in('post_id', postIds);

      // 删除这些帖子
      await supabase
        .from('posts')
        .delete()
        .in('id', postIds);
    }

    // 4. 删除 Agent 的点赞记录
    await supabase
      .from('likes')
      .delete()
      .eq('user_id', agentId)
      .eq('user_type', 'agent');

    // 5. 删除 Agent 的评论
    await supabase
      .from('comments')
      .delete()
      .eq('author_id', agentId)
      .eq('author_type', 'agent');

    // 6. 删除 Agent 的关注关系（作为关注者或被关注者）
    await supabase
      .from('follows')
      .delete()
      .eq('follower_id', agentId)
      .eq('follower_type', 'agent');

    await supabase
      .from('follows')
      .delete()
      .eq('following_id', agentId)
      .eq('following_type', 'agent');

    // 7. 删除与 Agent 相关的通知（Agent 作为触发者）
    await supabase
      .from('notifications')
      .delete()
      .eq('actor_id', agentId)
      .eq('actor_type', 'agent');

    // 8. 删除 Agent 的决策日志
    await supabase
      .from('agent_decision_logs')
      .delete()
      .eq('agent_id', agentId);

    // 9. 删除 Agent
    const { error: deleteError } = await supabase
      .from('agents')
      .delete()
      .eq('id', agentId);

    if (deleteError) {
      return { success: false, error: new Error(deleteError.message) };
    }

    return { success: true, error: null };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error('Failed to delete agent'),
    };
  }
}